import encontroImagemService from "@/service/encontroImagemService";
import { ref } from "vue";
import { usePagination } from "@/composables/usePagination";

export function useEncontroImagem() {
  const imagens = ref([]);
  const imagem = ref(null);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const isDownloading = ref(false);
  const pagination = usePagination({
    initialPage: 0,
    pageSize: 6,
    baseIndex: 0,
  });

  const fetchImagensPublicas = async (page = 0, size = 12, listagemAleatoria = false) => {
    isLoading.value = true;
    try {
      const data = await encontroImagemService.getEncontroImagemPublica(
        page,
        size,
        listagemAleatoria
      );
      if (data && Array.isArray(data.content)) {
        imagens.value = data.content;
        pagination.setPaginationInfo(data);
      } else if (Array.isArray(data)) {
        pagination.setPaginationInfo(data);
        pagination.totalPages.value = Math.max(Math.ceil(data.length / size), 1);
        pagination.currentPage.value = page;
        if (data.length > size) {
          imagens.value = data.slice(page * size, (page + 1) * size);
        } else {
          imagens.value = data;
        }
      } else {
        imagens.value = [];
        pagination.resetPagination();
      }
      return data;
    } catch (error) {
      console.error("Erro ao buscar imagens públicas:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchImagensByEncontro = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    encontroId,
    isPublic = false
  ) => {
    isLoading.value = true;
    try {
      let data;
      if (isPublic) {
        data =
          await encontroImagemService.getEncontroImagemPublicaByEncontroId(
            encontroId,
            page,
            size
          );
      } else {
        data = await encontroImagemService.getEncontroImagemByEncontroId(
          encontroId,
          page,
          size
        );
      }

      if (data && Array.isArray(data.content)) {
        imagens.value = data.content;
        pagination.setPaginationInfo(data);
      } else if (Array.isArray(data)) {
        pagination.setPaginationInfo(data);
        pagination.totalPages.value = Math.max(Math.ceil(data.length / size), 1);
        pagination.currentPage.value = page;
        if (data.length > size) {
          imagens.value = data.slice(page * size, (page + 1) * size);
        } else {
          imagens.value = data;
        }
      } else {
        imagens.value = [];
        pagination.resetPagination();
      }
      return data;
    } catch (error) {
      console.error("Erro ao buscar imagens por encontro:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchImagemByAcaoId = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    acaoId,
    isPublic = false
  ) => {
    isLoading.value = true;
    try {
      let data;
      if (isPublic) {
        data =
          await encontroImagemService.getEncontroImagemPublicaByAcaoId(
            acaoId,
            page,
            size
          );
      } else {
        data = await encontroImagemService.getEncontroImagemByAcaoId(
          acaoId,
          page,
          size
        );
      }

      if (data && Array.isArray(data.content)) {
        imagens.value = data.content;
        pagination.setPaginationInfo(data);
      } else if (Array.isArray(data)) {
        pagination.setPaginationInfo(data);
        pagination.totalPages.value = Math.max(Math.ceil(data.length / size), 1);
        pagination.currentPage.value = page;
        if (data.length > size) {
          imagens.value = data.slice(page * size, (page + 1) * size);
        } else {
          imagens.value = data;
        }
      } else {
        imagens.value = [];
        pagination.resetPagination();
      }
      return data;
    } catch (error) {
      console.error("Erro ao buscar imagens por ação:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchImagemById = async (encontroImagemId) => {
    isLoading.value = true;
    try {
      const data =
        await encontroImagemService.getEncontroImagemById(encontroImagemId);
      imagem.value = data;
    } catch (error) {
      console.error("Erro ao buscar imagem por ID:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const saveImagem = async (encontroId, imagemData) => {
    isSaving.value = true;
    try {
      const data = await encontroImagemService.postEncontroImagem(
        encontroId,
        imagemData
      );
      return data;
    } catch (error) {
      console.error("Erro ao salvar imagem:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const saveLoteImagens = async (encontroId, imagensData) => {
    isSaving.value = true;
    try {
      const data = await encontroImagemService.postLoteEncontroImagem(
        encontroId,
        imagensData
      );
      return data;
    } catch (error) {
      console.error("Erro ao salvar lote de imagens:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const deleteImagem = async (encontroImagemId) => {
    isLoading.value = true;
    try {
      const data =
        await encontroImagemService.deleteEncontroImagem(encontroImagemId);
      return data;
    } catch (error) {
      console.error("Erro ao deletar imagem:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const modificarVisibilidadeImagem = async (encontroImagemId, isPublica) => {
    isLoading.value = true;
    try {
      const data =
        await encontroImagemService.modificarVisibilidadeEncontroImagem(
          encontroImagemId,
          isPublica
        );
      return data;
    } catch (error) {
      console.error("Erro ao modificar visibilidade da imagem:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    imagens,
    imagem,
    isLoading,
    isSaving,
    isDownloading,
    pagination,
    fetchImagensByEncontro,
    fetchImagemByAcaoId,
    fetchImagensPublicas,
    fetchImagemById,
    saveImagem,
    saveLoteImagens,
    deleteImagem,
    modificarVisibilidadeImagem,
  };
}
